import type { UseCasePageData, UseCaseSection } from './useCaseQueries'
import { sectionHasContent } from './useCaseSectionGuards'

function makeKey(prefix: string, index: number) {
  return `legacy-${prefix}-${index}`
}

function synthesizeFromLegacy(page: UseCasePageData): UseCaseSection[] {
  const sections: UseCaseSection[] = []

  if (page.hero && (page.hero.headingMain || page.hero.headingAccent || page.hero.body)) {
    sections.push({
      _key: makeKey('hero', 0),
      _type: 'ucHero',
      eyebrow: page.hero.eyebrow,
      headingMain: page.hero.headingMain,
      headingAccent: page.hero.headingAccent,
      body: page.hero.body,
      primaryCtaLabel: page.hero.primaryCtaLabel,
      primaryCtaUrl: page.hero.primaryCtaUrl,
      secondaryCtaLabel: page.hero.secondaryCtaLabel,
      secondaryCtaUrl: page.hero.secondaryCtaUrl,
      footnote: page.hero.footnote,
      image: page.hero.media,
    })
  }

  if (page.problem && (page.problem.heading || page.problem.body)) {
    sections.push({
      _key: makeKey('problem', 0),
      _type: 'ucProblemStats',
      sceneLabel: page.problem.eyebrow,
      heading: page.problem.heading,
      quote: page.problem.body,
    })
  }

  if (page.workflow?.steps?.length) {
    page.workflow.steps.forEach((step, i) => {
      sections.push({
        _key: makeKey('workflow', i),
        _type: 'ucWorkflowStep',
        sceneLabel: i === 0 ? page.workflow?.eyebrow : undefined,
        stepNumber: step.stepNumber,
        kicker: step.title,
        heading: i === 0 ? page.workflow?.heading : step.title,
        body: step.description,
      })
    })
  }

  if (page.comparisonTable?.table?.rows?.length) {
    sections.push({
      _key: makeKey('comparison', 0),
      _type: 'ucComparisonTable',
      eyebrow: page.comparisonTable.eyebrow,
      heading: page.comparisonTable.heading,
      table: page.comparisonTable.table,
    })
  }

  if (page.useCasesGrid?.cards?.length) {
    sections.push({
      _key: makeKey('grid', 0),
      _type: 'ucFormatGrid',
      heading: page.useCasesGrid.heading,
      cards: page.useCasesGrid.cards.map((card) => ({
        tag: card.badgeLabel ?? '',
        title: card.title,
        description: card.description,
        image: card.image,
      })),
    })
  }

  if (page.features?.features?.length) {
    sections.push({
      _key: makeKey('features', 0),
      _type: 'ucFeatures',
      eyebrow: page.features.eyebrow,
      heading: page.features.heading,
      features: page.features.features,
    })
  }

  if (page.pricingCallout) {
    sections.push({
      _key: makeKey('pricing', 0),
      _type: 'ucPricingCallout',
      eyebrow: page.pricingCallout.eyebrow,
      heading: page.pricingCallout.heading,
      body: page.pricingCallout.body,
      ctaLabel: page.pricingCallout.ctaLabel,
      ctaUrl: page.pricingCallout.ctaUrl,
    })
  }

  if (page.faq?.items?.length) {
    sections.push({
      _key: makeKey('faq', 0),
      _type: 'ucFaq',
      eyebrow: page.faq.eyebrow,
      heading: page.faq.heading,
      headingAccent: page.faq.headingAccent,
      items: page.faq.items,
    })
  }

  if (page.ctaBanner) {
    sections.push({
      _key: makeKey('cta', 0),
      _type: 'ucCtaBanner',
      eyebrow: page.ctaBanner.eyebrow,
      headingMain: page.ctaBanner.headingMain,
      headingAccent: page.ctaBanner.headingAccent,
      subtext: page.ctaBanner.subtext,
      primaryCtaLabel: page.ctaBanner.primaryCtaLabel,
      primaryCtaUrl: page.ctaBanner.primaryCtaUrl,
      secondaryCtaLabel: page.ctaBanner.secondaryCtaLabel,
      secondaryCtaUrl: page.ctaBanner.secondaryCtaUrl,
      footnote: page.ctaBanner.footnote,
    })
  }

  return sections
}

export function normalizeUseCasePage(page: UseCasePageData): UseCaseSection[] {
  const fromSections = page.sections?.filter(sectionHasContent) ?? []
  if (fromSections.length > 0) return fromSections
  return synthesizeFromLegacy(page).filter(sectionHasContent)
}

export type NormalizedUseCasePage = UseCasePageData & {
  normalizedSections: UseCaseSection[]
}

export function withNormalizedSections(page: UseCasePageData): NormalizedUseCasePage {
  return {
    ...page,
    normalizedSections: normalizeUseCasePage(page),
  }
}
